/**
 * Format detection and evidence reconciliation.
 *
 * §10 step 4: "It verifies the extension, MIME type, magic bytes, file
 * signature, and internal structure when applicable."
 *
 * The rule this module enforces: **bytes outrank claims**. A filename and a
 * declared MIME type are assertions made by whichever app handed us the file;
 * the signature is evidence. When they disagree we follow the evidence and
 * record the disagreement, because a `.jpg` that is really a ZIP is either a
 * mistake worth telling the user about or an attack worth refusing (§30).
 */
import type { FormatSpec } from '../formats/support.js';
import { FORMATS_BY_EXTENSION, FORMATS_BY_ID, FORMATS_BY_MIME } from '../formats/catalog.js';
import { MAX_SIGNATURE_REACH, findPdfHeaderOffset, matchSignatures } from './signatures.js';

/** MIME types that carry no information; messaging apps send these constantly. */
const OPAQUE_MIMES = new Set([
  'application/octet-stream', 'application/unknown', 'binary/octet-stream',
  '', 'unknown/unknown', '*/*',
]);

export type Confidence =
  /** Signature identified the format outright. */
  | 'confirmed'
  /** Signature identified a container and structure inspection resolved it. */
  | 'resolved'
  /** No signature, but extension and/or MIME agree and the format is text-like. */
  | 'assumed'
  /** Nothing identified it. */
  | 'unknown';

export type WarningCode =
  | 'empty-file'
  | 'truncated'
  | 'extension-mismatch'
  | 'mime-mismatch'
  | 'no-signature'
  | 'container-unresolved'
  | 'macros-present'
  | 'encrypted'
  | 'sensitive-content'
  | 'unsupported-format';

export interface DetectionWarning {
  readonly code: WarningCode;
  /** i18n key — §25 forbids hardcoded user-facing strings. */
  readonly messageKey: string;
  /** Non-localised technical detail for the file-information screen (§31). */
  readonly detail?: string;
}

export interface DetectionEvidence {
  /** Format id implied by the strongest byte signature, if any. */
  readonly bySignature: string | null;
  /** Format id implied by the filename extension, if any. */
  readonly byExtension: string | null;
  /** Format id implied by the declared MIME type, if any. */
  readonly byMime: string | null;
  /** The declared MIME as received, for display (§31 "Detected MIME type"). */
  readonly declaredMime: string | null;
  /** Byte offset where the format actually starts (non-zero for tolerant PDFs). */
  readonly headerOffset: number;
}

export interface DetectionResult {
  readonly formatId: string | null;
  readonly spec: FormatSpec | null;
  readonly confidence: Confidence;
  readonly evidence: DetectionEvidence;
  /** Set when the format is a container whose real identity needs entry names. */
  readonly containerKind: 'zip' | 'ole2' | 'riff' | 'ebml' | 'isobmff' | 'tiff' | null;
  readonly needsContainerInspection: boolean;
  readonly warnings: readonly DetectionWarning[];
  /** §22/§29: treat contents as credentials — keep out of logs and thumbnails. */
  readonly sensitive: boolean;
}

export interface DetectionInput {
  /** Original filename as received. May be absent (Android content:// URIs). */
  readonly filename?: string | undefined;
  /** MIME type claimed by the source app. Usually unreliable. */
  readonly declaredMime?: string | undefined;
  /** At least {@link MAX_SIGNATURE_REACH} bytes when the file is that long. */
  readonly head: Uint8Array;
  /** Total file size when known, to spot truncated downloads (§35). */
  readonly size?: number | undefined;
}

/** Filenames whose contents are credentials regardless of extension (§22). */
const SENSITIVE_NAMES = new Set([
  '.env', '.env.local', '.env.production', '.env.development', '.env.test',
  '.netrc', '_netrc', '.pgpass', '.htpasswd', 'credentials', 'id_rsa', 'id_ed25519',
]);
const SENSITIVE_EXTENSIONS = new Set(['.env', '.pem', '.key', '.p12', '.pfx', '.keystore', '.jks']);

export function extensionOf(filename: string | undefined): string | null {
  if (!filename) return null;
  // Take the base name first so a dot in a directory path is not mistaken for
  // an extension. Both separators appear because Android hands us POSIX paths
  // while Windows hands us backslashes.
  const base = filename.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return null; // no dot, or a dotfile like ".env" with no extension
  return base.slice(dot).toLowerCase();
}

/**
 * Catalog lookup key for a filename.
 *
 * Distinct from {@link extensionOf} because a file named `.env` has no
 * extension in the ordinary sense — but `.env` is exactly the key the catalog
 * registers it under, and the same is true of `.gitignore`, `.editorconfig`
 * and every other dotfile. Treating those as "no extension" left them
 * unmatched, which is how a credentials file stopped being recognised as one.
 */
export function formatKeyOf(filename: string | undefined): string | null {
  const ext = extensionOf(filename);
  if (ext) return ext;
  if (!filename) return null;
  const base = (filename.split(/[\\/]/).pop() ?? '').toLowerCase();
  // A leading dot and no other dot: the whole name is the key.
  return base.startsWith('.') && base.lastIndexOf('.') === 0 ? base : null;
}

function baseNameOf(filename: string | undefined): string {
  return (filename?.split(/[\\/]/).pop() ?? '').toLowerCase();
}

function isSensitive(filename: string | undefined, ext: string | null): boolean {
  const base = baseNameOf(filename);
  if (!base) return false;
  if (SENSITIVE_NAMES.has(base)) return true;
  if (base.startsWith('.env.')) return true;
  return ext !== null && SENSITIVE_EXTENSIONS.has(ext);
}

/** Normalise a declared MIME: strip parameters, lower-case, treat opaque as absent. */
function normaliseMime(declared: string | undefined): string | null {
  if (!declared) return null;
  const bare = declared.split(';')[0]?.trim().toLowerCase() ?? '';
  return OPAQUE_MIMES.has(bare) ? null : bare;
}

/**
 * Does this buffer look like text? Used only as a last resort when no signature
 * matched, so that an extension-less `README` still opens instead of being
 * declared unknown.
 */
export function looksLikeText(head: Uint8Array): boolean {
  if (head.length === 0) return false;
  const sample = head.subarray(0, Math.min(head.length, 4096));
  let control = 0;
  for (const byte of sample) {
    if (byte === 0) return false; // NUL — binary
    // Allow tab, LF, CR, FF, ESC; count other C0 controls as evidence of binary.
    if (byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d && byte !== 0x0c && byte !== 0x1b) {
      control++;
    }
  }
  return control / sample.length < 0.05;
}

/**
 * Stage 1 detection: everything decidable from the file head plus the claims
 * made about the file. Cheap, synchronous, and safe to run before any parser
 * touches the bytes.
 */
export function detect(input: DetectionInput): DetectionResult {
  const warnings: DetectionWarning[] = [];
  const { head } = input;
  const ext = extensionOf(input.filename);
  const mime = normaliseMime(input.declaredMime);
  const sensitive = isSensitive(input.filename, ext);
  if (sensitive) {
    warnings.push({
      code: 'sensitive-content', messageKey: 'detect.warning.sensitiveContent',
      detail: 'Excluded from logs, thumbnails, recent files and crash reports (§22, §29).',
    });
  }

  const lookupKey = formatKeyOf(input.filename);
  const extSpec = lookupKey ? FORMATS_BY_EXTENSION.get(lookupKey) ?? null : null;
  const mimeSpec = mime ? FORMATS_BY_MIME.get(mime) ?? null : null;

  const emptyEvidence = (bySignature: string | null, headerOffset = 0): DetectionEvidence => ({
    bySignature,
    byExtension: extSpec?.id ?? null,
    byMime: mimeSpec?.id ?? null,
    declaredMime: input.declaredMime ?? null,
    headerOffset,
  });

  if (input.size === 0 || head.length === 0) {
    warnings.push({ code: 'empty-file', messageKey: 'detect.warning.emptyFile' });
    return {
      formatId: extSpec?.id ?? null, spec: extSpec, confidence: 'unknown',
      evidence: emptyEvidence(null), containerKind: null,
      needsContainerInspection: false, warnings, sensitive,
    };
  }

  if (input.size !== undefined && input.size > 0 && head.length < Math.min(input.size, MAX_SIGNATURE_REACH)) {
    // We were given less than we asked for and the file is longer — the caller
    // may be streaming, so this is informational rather than an error.
    warnings.push({
      code: 'truncated', messageKey: 'detect.warning.shortHead',
      detail: `head=${head.length}B size=${input.size}B`,
    });
  }

  const matches = matchSignatures(head);
  let best = matches[0] ?? null;
  let headerOffset = 0;

  // Tolerant PDF: bytes may precede %PDF-.
  if (!best || best.weight < 100) {
    const pdfAt = findPdfHeaderOffset(head);
    if (pdfAt > 0) {
      best = { format: 'pdf', weight: 100, container: undefined };
      headerOffset = pdfAt;
    }
  }

  if (!best) {
    // No byte evidence. Fall back to claims, but say so.
    const fallback = extSpec ?? mimeSpec;
    if (fallback && (fallback.family === 'code' || fallback.family === 'data'
      || fallback.id === 'txt' || fallback.id === 'markdown' || fallback.id === 'csv'
      || fallback.id === 'tsv' || fallback.id === 'svg' || fallback.id === 'html')) {
      // Text formats genuinely have no magic bytes; trusting the extension here
      // is correct rather than sloppy.
      const textual = looksLikeText(head);
      if (!textual) {
        warnings.push({
          code: 'extension-mismatch', messageKey: 'detect.warning.notTextDespiteExtension',
          detail: `extension ${ext} implies text but the content is binary`,
        });
      }
      return {
        formatId: fallback.id, spec: fallback, confidence: textual ? 'assumed' : 'unknown',
        evidence: emptyEvidence(null), containerKind: null,
        needsContainerInspection: false, warnings, sensitive,
      };
    }

    warnings.push({ code: 'no-signature', messageKey: 'detect.warning.noSignature' });
    if (looksLikeText(head)) {
      const txt = FORMATS_BY_ID.get('txt') ?? null;
      return {
        formatId: txt?.id ?? null, spec: txt, confidence: 'assumed',
        evidence: emptyEvidence(null), containerKind: null,
        needsContainerInspection: false, warnings, sensitive,
      };
    }
    return {
      formatId: fallback?.id ?? null, spec: fallback, confidence: 'unknown',
      evidence: emptyEvidence(null), containerKind: null,
      needsContainerInspection: false, warnings, sensitive,
    };
  }

  const evidence = emptyEvidence(best.container ? null : best.format, headerOffset);

  if (best.container) {
    // Identified only as a container. The catalog has no entry for bare
    // containers, so the real format comes from inspectZipEntries/inspectOle2.
    return {
      formatId: null, spec: null, confidence: 'unknown', evidence,
      containerKind: best.container, needsContainerInspection: true,
      warnings, sensitive,
    };
  }

  const sigSpec = FORMATS_BY_ID.get(best.format) ?? null;

  // Reconcile claims against evidence.
  if (sigSpec && extSpec && extSpec.id !== sigSpec.id) {
    warnings.push({
      code: 'extension-mismatch', messageKey: 'detect.warning.extensionMismatch',
      detail: `extension "${ext}" implies ${extSpec.id}; content is ${sigSpec.id}`,
    });
  }
  if (sigSpec && mimeSpec && mimeSpec.id !== sigSpec.id) {
    warnings.push({
      code: 'mime-mismatch', messageKey: 'detect.warning.mimeMismatch',
      detail: `declared "${mime}" implies ${mimeSpec.id}; content is ${sigSpec.id}`,
    });
  }
  if (!sigSpec) {
    warnings.push({
      code: 'unsupported-format', messageKey: 'detect.warning.unsupportedFormat',
      detail: `signature matched "${best.format}" which is not in the catalog`,
    });
  }

  return {
    formatId: sigSpec?.id ?? null, spec: sigSpec,
    confidence: sigSpec ? (best.weight >= 90 ? 'confirmed' : 'assumed') : 'unknown',
    evidence, containerKind: null, needsContainerInspection: false,
    warnings, sensitive,
  };
}

/* ------------------------------------------------------------------ *
 * Stage 2 — container structure inspection (§30 "internal-structure validation")
 * ------------------------------------------------------------------ */

export interface ContainerInspection {
  readonly formatId: string | null;
  /** §31 "Whether macros or scripts were blocked". */
  readonly macrosPresent: boolean;
  readonly encrypted: boolean;
  readonly warnings: readonly DetectionWarning[];
}

/** Marker paths that identify an OOXML/OPC package by its part layout. */
const ZIP_PART_MARKERS: readonly (readonly [test: (n: Set<string>) => boolean, format: string])[] = [
  [(n) => n.has('word/document.xml'), 'docx'],
  [(n) => n.has('ppt/presentation.xml'), 'pptx'],
  [(n) => n.has('xl/workbook.xml') || n.has('xl/workbook.bin'), 'xlsx'],
  // HWPX: OWPML package. version.xml at the root plus a Contents directory.
  [(n) => n.has('version.xml') && [...n].some((e) => e.startsWith('Contents/')), 'hwpx'],
  [(n) => n.has('meta.json') && [...n].some((e) => e.startsWith('pages/')), 'sketch'],
];

/** `mimetype` entry values used by ODF, EPUB and HWPX packages. */
const ZIP_MIMETYPE_MAP: Readonly<Record<string, string>> = {
  'application/epub+zip': 'epub',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/hwp+zip': 'hwpx',
  'application/vnd.apple.pages': 'pages',
  'application/vnd.apple.numbers': 'numbers',
  'application/vnd.apple.keynote': 'keynote',
};

/**
 * Resolve a ZIP container to a concrete format from its entry names.
 *
 * @param entryNames All entry paths, as stored (forward slashes).
 * @param mimetypeValue Contents of the `mimetype` entry when present. ODF, EPUB
 *   and HWPX store it first and uncompressed precisely so it can be read cheaply.
 * @param declaredExtension Used only to disambiguate iWork bundles, whose
 *   internal layout is identical across Pages/Numbers/Keynote.
 */
export function inspectZipEntries(
  entryNames: readonly string[],
  mimetypeValue?: string | undefined,
  declaredExtension?: string | null,
): ContainerInspection {
  const warnings: DetectionWarning[] = [];
  const names = new Set(entryNames);

  const macrosPresent = entryNames.some((n) =>
    n === 'word/vbaProject.bin' || n === 'xl/vbaProject.bin'
    || n === 'ppt/vbaProject.bin' || n.endsWith('/vbaProject.bin'));
  if (macrosPresent) {
    warnings.push({
      code: 'macros-present', messageKey: 'detect.warning.macrosBlocked',
      detail: 'vbaProject.bin present; preserved on save but never executed (§15, §30).',
    });
  }

  // OOXML encryption wraps the package in an OLE2 container, so an encrypted
  // OOXML file never reaches here as a ZIP. ODF encryption is per-entry and is
  // declared in META-INF/manifest.xml.
  const encrypted = names.has('META-INF/manifest.xml') && entryNames.some((n) => n.includes('encrypted'));
  if (encrypted) {
    warnings.push({ code: 'encrypted', messageKey: 'detect.warning.encrypted' });
  }

  const byMimetype = mimetypeValue ? ZIP_MIMETYPE_MAP[mimetypeValue.trim()] : undefined;
  if (byMimetype) {
    // iWork bundles all declare distinct mimetypes, so this already disambiguates.
    return { formatId: byMimetype, macrosPresent, encrypted, warnings };
  }

  for (const [test, format] of ZIP_PART_MARKERS) {
    if (test(names)) return { formatId: format, macrosPresent, encrypted, warnings };
  }

  // iWork without a mimetype entry: identical IWA layout, so fall back to the
  // extension. This is the one place a claim is allowed to decide, and only
  // between three formats we already know it to be.
  if (entryNames.some((n) => n.startsWith('Index/') && n.endsWith('.iwa'))) {
    const iwork = declaredExtension === '.numbers' ? 'numbers'
      : declaredExtension === '.key' ? 'keynote'
        : declaredExtension === '.pages' ? 'pages' : null;
    if (iwork) return { formatId: iwork, macrosPresent, encrypted, warnings };
  }

  // A plain ZIP archive.
  return { formatId: 'zip', macrosPresent, encrypted, warnings };
}

/** OLE2 stream names that identify the document type inside a CFB container. */
export function inspectOle2Streams(streamNames: readonly string[]): ContainerInspection {
  const warnings: DetectionWarning[] = [];
  const lower = streamNames.map((n) => n.toLowerCase());
  const has = (needle: string) => lower.some((n) => n === needle || n.endsWith(`/${needle}`));

  const macrosPresent = lower.some((n) => n.includes('vba') || n === 'macros');
  if (macrosPresent) {
    warnings.push({
      code: 'macros-present', messageKey: 'detect.warning.macrosBlocked',
      detail: 'VBA storage present in the OLE2 container; never executed (§30).',
    });
  }

  // OOXML files encrypted with agile encryption are OLE2 wrappers around the
  // real package. Detecting this is how §34's "password protected" message and
  // §31's encryption status get their value.
  const encrypted = has('encryptedpackage') || has('encryptioninfo');
  if (encrypted) {
    warnings.push({ code: 'encrypted', messageKey: 'detect.warning.encrypted' });
    return { formatId: null, macrosPresent, encrypted, warnings };
  }

  if (has('worddocument')) return { formatId: 'doc', macrosPresent, encrypted, warnings };
  if (has('workbook') || has('book')) return { formatId: 'xls', macrosPresent, encrypted, warnings };
  if (has('powerpoint document')) return { formatId: 'ppt', macrosPresent, encrypted, warnings };
  // HWP 5.0 stores a FileHeader stream whose payload begins "HWP Document File".
  if (has('fileheader') && has('docinfo')) return { formatId: 'hwp', macrosPresent, encrypted, warnings };

  warnings.push({
    code: 'container-unresolved', messageKey: 'detect.warning.containerUnresolved',
    detail: `OLE2 streams: ${streamNames.slice(0, 10).join(', ')}`,
  });
  return { formatId: null, macrosPresent, encrypted, warnings };
}

/**
 * Resolve an EBML container to WebM or Matroska from its `DocType`.
 *
 * The magic bytes only say "EBML"; the concrete format is the `DocType` string
 * (`0x4282`) inside the header, which sits within the first few dozen bytes and
 * so is present in the head read — no whole-file read is needed. A file that
 * calls itself `webm` is WebM; anything else EBML is treated as Matroska.
 */
export function inspectEbmlHeader(head: Uint8Array): ContainerInspection {
  const warnings: DetectionWarning[] = [];
  // Find the DocType element id 0x42 0x82, then its (short) size and string.
  const limit = Math.min(head.length, 256);
  for (let i = 0; i + 3 < limit; i++) {
    if (head[i] !== 0x42 || head[i + 1] !== 0x82) continue;
    const sizeByte = head[i + 2]!;
    // DocType is a short string; its size vint is one byte (marker 0x80).
    if ((sizeByte & 0x80) === 0) continue;
    const len = sizeByte & 0x7f;
    if (len === 0 || i + 3 + len > head.length) continue;
    let docType = '';
    for (let j = 0; j < len; j++) docType += String.fromCharCode(head[i + 3 + j]!);
    docType = docType.replace(/\0+$/, '');
    if (docType === 'webm') return { formatId: 'webm', macrosPresent: false, encrypted: false, warnings };
    if (docType === 'matroska') return { formatId: 'mkv', macrosPresent: false, encrypted: false, warnings };
    break;
  }
  // An EBML file whose DocType we could not read is left as its provisional id.
  return { formatId: null, macrosPresent: false, encrypted: false, warnings };
}

/**
 * Merge stage-1 and stage-2 results into the record shown on the file
 * information screen (§31).
 */
export function applyContainerInspection(
  stage1: DetectionResult,
  inspection: ContainerInspection,
): DetectionResult {
  const spec = inspection.formatId ? FORMATS_BY_ID.get(inspection.formatId) ?? null : null;
  const warnings = [...stage1.warnings, ...inspection.warnings];

  if (!spec) {
    return {
      ...stage1, formatId: inspection.formatId, spec: null, confidence: 'unknown',
      needsContainerInspection: false, warnings,
    };
  }

  // Stage 1 returns early for containers — it has nothing to reconcile against
  // yet — so both claims are checked here, once the real format is known.
  // Omitting the MIME check meant a DOCX arriving as `image/jpeg` was reported
  // as only an extension problem.
  const ext = stage1.evidence.byExtension;
  if (ext && ext !== spec.id) {
    warnings.push({
      code: 'extension-mismatch', messageKey: 'detect.warning.extensionMismatch',
      detail: `extension implies ${ext}; container structure is ${spec.id}`,
    });
  }

  const mime = stage1.evidence.byMime;
  if (mime && mime !== spec.id) {
    warnings.push({
      code: 'mime-mismatch', messageKey: 'detect.warning.mimeMismatch',
      detail: `declared "${stage1.evidence.declaredMime}" implies ${mime}; container structure is ${spec.id}`,
    });
  }

  return {
    ...stage1, formatId: spec.id, spec, confidence: 'resolved',
    needsContainerInspection: false,
    evidence: { ...stage1.evidence, bySignature: spec.id },
    warnings,
  };
}
